import { ReactComponent as PlusIcon } from '@/assets/icons/plus-icon.svg';
import IconButton from '@/views/components/Buttons/IconButton';
import SvgConverter from '@/views/components/Common/SvgConverter';
import SecondaryLabel from '@/views/components/Labels/SecondaryLabel';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Col, Row } from 'antd';
import { useCallback } from 'react';
import ColumnCard from './ColumnCard';

const SortableItem = ({
  id,
  data,
  index,
  availableColumns,
  selectedColumnValuesArray,
  isColumnsLoading,
  handleRemoveColumn,
  handleSelectValueChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <ColumnCard
        dragHandleListeners={listeners}
        availableColumns={availableColumns.filter(({ value }) =>
          data.columnValue === value ? true : !selectedColumnValuesArray.includes(value),
        )}
        isColumnsLoading={isColumnsLoading}
        handleRemoveColumn={handleRemoveColumn}
        index={index}
        selectedValue={data}
        handleSelectValueChange={handleSelectValueChange}
      />
    </div>
  );
};

export const ColumnOptions = ({
  availableColumns,
  isColumnsLoading,
  handleOnDragEnd,
  selectedColumns = [],
  handleAddNewColumn,
  handleRemoveColumn,
  handleSelectValueChange,
  selectedColumnValuesArray = [],
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = selectedColumns.findIndex((item) => item.id === active.id);
        const newIndex = selectedColumns.findIndex((item) => item.id === over.id);

        const reorderedColumns = arrayMove(selectedColumns, oldIndex, newIndex);

        handleOnDragEnd(reorderedColumns);
      }
    },
    [selectedColumns, handleOnDragEnd],
  );

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={selectedColumns.map((col) => col.id)}>
          {selectedColumns.map((data, index) => (
            <SortableItem
              key={data.id}
              id={data.id}
              data={data}
              index={index}
              availableColumns={availableColumns}
              selectedColumnValuesArray={selectedColumnValuesArray}
              isColumnsLoading={isColumnsLoading}
              handleRemoveColumn={handleRemoveColumn}
              handleSelectValueChange={handleSelectValueChange}
            />
          ))}
        </SortableContext>
      </DndContext>
      <IconButton onClick={handleAddNewColumn}>
        <Row align="middle" gutter={5}>
          <Col>
            <SvgConverter Icon={PlusIcon} className="d-flex" color="header" />
          </Col>
          <Col>
            <SecondaryLabel className="text-header f-16 fw-600">Add a column</SecondaryLabel>
          </Col>
        </Row>
      </IconButton>
    </div>
  );
};
